import { useEffect, useState } from 'react';
import { Alert, Stack, Typography, useTheme } from '@mui/material';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { getDashboard, type DashboardPayload } from '../lib/api';
import { WAF_YELLOW_DARK } from '../theme';
import { LoadingScreen } from '../components/LoadingScreen';
import { StatCard, SectionHeading, ActivityChart } from '../components/DashboardWidgets';

/**
 * Metrics — the activity/analytics charts that used to live at the bottom of the
 * dashboard. Pulled into Admin tools so the dashboard stays a clean entries view.
 */
export function Metrics() {
  const theme = useTheme();
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getDashboard()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'failed to load');
      });
    return () => { cancelled = true; };
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <LoadingScreen />;

  const users = data.users;
  const accentBlue = theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB';

  return (
    <Stack spacing={4}>
      <Typography variant="h3" component="h1" align="center">
        Metrics
      </Typography>

      {/* ───────────────────────── Change requests ───────────────────────── */}
      <Stack spacing={2}>
        <SectionHeading title="Change requests" />
        <ActivityChart
          title="Change request activity"
          subtitle="Submitted vs. reviewed per day — last 30 days"
          dates={data.change_request_activity.map((p) => p.date)}
          emptyHint="No change requests in this window yet."
          series={[
            {
              id: 'submitted',
              label: 'Submitted',
              data: data.change_request_activity.map((p) => p.submitted),
              color: WAF_YELLOW_DARK,
              area: true,
            },
            {
              id: 'reviewed',
              label: 'Reviewed',
              data: data.change_request_activity.map((p) => p.reviewed),
              color: accentBlue,
            },
          ]}
        />
      </Stack>

      {/* ───────────────────────── System activity ───────────────────────── */}
      <Stack spacing={2}>
        <SectionHeading title="System activity" />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
          <StatCard icon={<PeopleOutlinedIcon />} label="Total users" value={users.total} />
          <StatCard
            icon={<LoginOutlinedIcon />}
            label="Active users"
            value={users.active}
            total={users.total}
          />
          <StatCard
            icon={<ApartmentOutlinedIcon />}
            label="Architects"
            value={data.architect_firms}
          />
        </Stack>
        <ActivityChart
          title="Logins"
          subtitle="Daily active users (staff vs architects) — last 30 days"
          dates={data.login_activity.map((p) => p.date)}
          emptyHint="No logins recorded yet"
          series={[
            {
              id: 'staff',
              label: 'Staff',
              data: data.login_activity.map((p) => p.staff),
              color: WAF_YELLOW_DARK,
              area: true,
              stack: 'dau',
            },
            {
              id: 'architects',
              label: 'Architects',
              data: data.login_activity.map((p) => p.architects),
              color: accentBlue,
              area: true,
              stack: 'dau',
            },
          ]}
        />
      </Stack>
    </Stack>
  );
}
